import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next'
import Head from 'next/head'
import { Grid } from '@mui/material'
import { useRouter } from 'next/router'
import { NextPageLayout } from '@/utils/types/getLayout'
import Prefooter from '@/components/particles/prefooter/index'
import { selectPropertiesCount, selectPropertiesStatus, selectPropertiesResult } from '@/app/reducers/properties'
import { selectFilter, serializeFilterValue } from '@/app/reducers/filters'
import { STATUS } from '@/app/globalContext/globalContext.interfaces'
import { IFilterContext } from '@/app/globalContext/filterContext.interface'
import { ContainerStyled } from '@/components/templates/customMainContainer'
import { ListadoPropiedades } from '@/components/molecules/cardPropiedad'
import { OrdenarPor } from '@/components/atoms/select'
import { PaginationIndex } from '@/components/particles'
import { FooterIndex } from '@/components/particles/footer/index'
import { TextoSeo } from '@/components/atoms/textoSeo/TextoSeo'
import { NotFound } from '@/components/particles/notFound/index'
import { BannerSimple } from '@/components/particles/bannerSimple/BannerSimple'
import getPersonasLayout from '@/components/templates/menu/view/menuNuevo'
import { Carousel, Loader as GlobalLoader } from '@toctoc/design-system'
import { IBanner } from '@/components/templates/search/interfaces/IEstructuraPrincipal'
import { Filters } from '@/components/templates/filters/Filters'
import { getSchema } from '../layout/_schema'
import { getBreadcrumbSchema } from '../layout/_breadcrumbSchema'
import { Breadcrumbs } from '@/components/molecules/breadcrumbs'
import { GoogleTagManager } from '@/utils/browser/googleTagManager/GoogleTagManager'
import { useWindowSize } from '@/utils/hooks'
import { VWO } from '@/utils/VWO/VWO'
import { useBannerUpdater } from '../src/hooks/useBannerUpdater'
import { ListaSEOType } from '@/utils/types'
import { generateBajadaTitulo, getBreadcrumbsSegments, getListaSeoStaticProps } from '@/utils/listaSeo'
import { usePropertySearch } from 'src/hooks/usePropertySearch'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import React from 'react'
import { selectSessionState, setTempLogInData } from '@/app/reducers/session'
import { selectLeadState, sendLead, setSelectedPropertyData, setShowContactStatusModal, setShowGuestSignUpModal, setShowQuoteStatusModal } from '@/app/reducers/lead'
import { GuestSignUpModal } from '@/components/organisms/guestSignUpModal/GuestSignUpModal'
import { QuoteStatusModal } from '@/components/organisms/quoteStatusModal/QuoteStatusModal'
import { ContactStatusModal } from '@/components/organisms/contactStatusModal/ContactStatusModal'
import { IPropertyCard } from '@/components/templates/search/interfaces'
import { LeadRequest } from '@/components/templates/search/interfaces/ILeadRequest'
import { TempUserData } from '@/components/templates/search/interfaces/ISessionData'

export const getStaticProps: GetStaticProps<ListaSEOType> = async (ctx) =>{
	const resp = await getListaSeoStaticProps(ctx, []);

	if(resp === undefined){
		return {
			notFound: true,
		}
	}

	return {
		props: {
			...resp,
		},
		revalidate: 600 // every 10 minutes check for new content
	};
}

export const getStaticPaths = (async () => {
	const pathList = [["venta"], ["arriendo"]]
	const paths = pathList.map(paths => (
		{
			params: {
				pathListaSeo: paths, // Array path of url /venta/casa/metropolitana/la-florida [ "venta", "casa", "metropolitana", "la-florida"]
			},
		}
	));

	return {
		paths: paths,
		fallback: "blocking",
	}
}) satisfies GetStaticPaths

const ListaSEO: NextPageLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props: InferGetStaticPropsType<typeof getStaticProps>) => {
	const dispatch = useAppDispatch();
	const filterState = useAppSelector(selectFilter);
	const filterStateSerialized = serializeFilterValue(filterState)
	const propertiesCountState = useAppSelector(selectPropertiesCount);
	const propertiesStatusState = useAppSelector(selectPropertiesStatus);
	const propertiesList = useAppSelector(selectPropertiesResult);
	const leadState = useAppSelector(selectLeadState);
	const { status: leadStatus, leadStatusInfo, showGuestSignUpModal, selectedPropertyData, showQuoteStatusModal, showContactStatusModal } = leadState ?? {};
	const sessionInfo = useAppSelector(selectSessionState);
	const { userData } = sessionInfo ?? {};
	const router = useRouter();
	const { pathListaSeo } = router.query;
	const { urlSegments, IS_PROD, listaSeoUrl, quoteAndContactEnabled } = props;
	
	// Extract values from flexible structure, with fallbacks to old rigid structure
	const { tof, tpf, region, comuna, chainSegment } = getBreadcrumbsSegments(urlSegments, pathListaSeo as string[]);
	const [value, setValue] = React.useState(1);
	const handleChange = (event: React.SyntheticEvent<Element>, newValue: number) => setValue(newValue);
	const isCardWide = value === 0
	const { estructuraPrincipal, filtrosBase, computedRegionSlug, footerData } = props;
	const { banners, filtros, idsRequeridos, seo } = estructuraPrincipal ?? {};
	const { title, description } = seo.meta ?? {}

	// Hook para actualizar banners dinámicamente basado en filtros
	const { 
		banners: dynamicBanners, 
		breadcrumbs: dynamicBreadcrumbs,
		seo: dynamicSeo,
		loading: bannersLoading, 
		error: bannersError 
	} = useBannerUpdater(
		filterState.filtros, 
		estructuraPrincipal.urlSlug, 
		banners,
		null, // breadcrumbs iniciales
		seo // seo inicial
	);

	// Usar contenido dinámico si está disponible, sino los originales
	const currentBanners = dynamicBanners || banners;
	const currentSeo = dynamicSeo || seo;
	const pathCanonical = pathListaSeo as string[] ?? []
	const urlCanonical = "/" + pathCanonical.join("/")
	const url = props.urlBase ? props.urlBase + urlCanonical : 'https://www.toctoc.com' + urlCanonical;
	const ssrFilterState = (props.initialReduxState?.FilterState ?? {}) as IFilterContext;
	const NEXT_PUBLIC_ID_VWO = props.NEXT_PUBLIC_ID_VWO;
	let ssrFilterStateSerialized = serializeFilterValue(ssrFilterState);
	ssrFilterStateSerialized += ssrFilterState.order + "" + ssrFilterState.page;

	const bajadaTitulo = generateBajadaTitulo(propertiesCountState, filtrosBase);

	const anchoAlto = useWindowSize();
	const mobileDevice = () => ((anchoAlto.width as any) ?? 0) <= 767;

	const { triggerSearchRef: triggerSearch } = usePropertySearch(
		filterState,
		filterStateSerialized,
		{
			actionDelay: 800,
			firstSearchDelay: 0,
			initialFilterStateSerialized: ssrFilterStateSerialized,
			autoExecute: true,
		}
	);

	const propsSwiper = {
		navigation: false,
		spaceBetween: 16,
		slidesPerView: 1,
		loop: false,
		autoplay: true,
		delay: 7000,
		breakpoints: {
			600: {
				slidesPerView: 2,
			},
			980: {
				slidesPerView: 3,
			}
		},
		pagination: {
			clickable: true
		}
	};

	// Get dynamic labels from current filters only (no fallback to URL)
	const comunaLabel = filterState.filtros.find(f => f.id === "comuna")?.values[0]?.label;
	const regionLabel = filterState.filtros.find(f => f.id === "region")?.values[0]?.label;

	// If no filters are active, use values from filtrosBase (initial state)
	const fallbackComunaLabel = filterState.filtros.length === 0 ? 
		filtrosBase.find(x => x.id === 'comuna')?.values[0]?.label : null;
	const fallbackRegionLabel = filterState.filtros.length === 0 ? 
		filtrosBase.find(x => x.id === 'region')?.values[0]?.label : null;

	// Use dynamic filter labels, fallback to initial state, but NOT to URL segments
	const displayRegion = regionLabel || fallbackRegionLabel || '';
	const displayComuna = comunaLabel || fallbackComunaLabel || '';

	// Modal dispatchers
	const guestSignUpModalHandler = (showModal: boolean) => dispatch(setShowGuestSignUpModal(showModal));
	const quoteStatusModalHandler = (showModal: boolean) => dispatch(setShowQuoteStatusModal(showModal));
	const contactStatusModalHandler = (showModal: boolean) => dispatch(setShowContactStatusModal(showModal));
	const selectedPropertyDataHandler = (selectedPropertyData: IPropertyCard) => dispatch(setSelectedPropertyData(selectedPropertyData));
	// Lead dispatchers
	const generateLead = (leadData: LeadRequest) => dispatch(sendLead(leadData));
	// Session dispatchers
	const saveTempLogInData = (leadData: TempUserData) => dispatch(setTempLogInData(leadData));

	return (
		<>
			<Head>
				<title>{title}</title>
				<meta name="description" content={description} />
				<meta property="og:title" content={title} />
				<meta property="og:description" content={description} />
				<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: getSchema(propertiesList) }} />
				<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: getBreadcrumbSchema(props.urlBase, tof, tpf, region, comuna, chainSegment) }} />
				<link rel="canonical" href={url}></link>
			</Head>
			<ContainerStyled>
				<main>
					<Breadcrumbs urlBase={props.urlBase} tipoOperacionFamilia={tof} tipoPropiedadFamilia={tpf} region={displayRegion} comuna={displayComuna} isMobile={mobileDevice()} comunaPath={comuna} regionPath={region} chain={chainSegment} data-origen={url} />
					<GoogleTagManager idGTM={props.idGTM} />
					<VWO idVWO={NEXT_PUBLIC_ID_VWO}/>
					<div className='cTT' >
						<h1 className='ttBPLIndex'>{currentSeo.title}</h1>
						<p className='bajadaBPLIndex'>{bajadaTitulo}</p>
						{/* {!IS_PROD && bannersLoading && (
							<div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
								🔄 Actualizando contenido dinámico...
							</div>
						)}
						{!IS_PROD && bannersError && (
							<div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
								⚠️ Error al cargar contenido dinámico
							</div>
						)}
						{!IS_PROD && dynamicSeo && (
							<div style={{ fontSize: '12px', color: '#28a745', marginTop: '4px' }}>
								✅ Título y bajada actualizados dinámicamente
							</div>
						)} */}
					</div>
					<Carousel
						{...propsSwiper}
						slideItems={<>{
							currentBanners?.superior?.map((x: IBanner, index: number) => {
								return <a key={x.order} target='_blank' href={x.url} id="banner-superior"><img src={x.imagenes.srcDefault} width={'100%'} style={{ border: "solid 1px #f4f4f4", borderRadius: "10px"}} alt={x.alt} data-apartado={`banner_lista_seo_superior_${index + 1}`} data-nombre-campana="nombre_campaña_superior" /></a>
							})
						}</>}
					/>
					<Grid container >
						<Grid container rowSpacing={{ xs: 1, md: 2 }} columnSpacing={2}>
							<Grid marginBottom={2} item xs={12} md={3} display={{ xs: 'block', md: 'block' }} order={{ xs: 1, md: 2 }}>
								<Filters triggerSearch={triggerSearch} filtros={filtros} idsRequeridos={idsRequeridos} isMobile={mobileDevice()} filtrosBase={filtrosBase} urlSlug={estructuraPrincipal.urlSlug} pathListaSeo={pathCanonical}/>
								<Grid item md={12} display={{ xs: 'none', md: 'block' }} >
									{!mobileDevice() && <TextoSeo seoText={seo.seoText} />}
									<BannerSimple lateral={currentBanners.lateral} />
								</Grid>
							</Grid>
							<Grid item xs={12} md={9} order={{ xs: 2 }}>
								<OrdenarPor totalPropiedades={propertiesCountState} value={value} handleChange={handleChange} tipoOperacionFamilia={tof} tipoPropiedadFamilia={tpf}/>
								{(propertiesStatusState === STATUS.pending || propertiesStatusState === STATUS.init) ? <GlobalLoader></GlobalLoader> :
									(propertiesCountState === 0 ?
										<NotFound /> : <ListadoPropiedades hideLeadButtons={true} cardWide={isCardWide} medio={currentBanners.medio} listaSeoUrl={pathCanonical.join('/')} sessionInfo={sessionInfo} guestSignUpModalHandler={guestSignUpModalHandler} selectedPropertyDataHandler={selectedPropertyDataHandler} generateLead={generateLead} quoteAndContactEnabled={quoteAndContactEnabled as boolean} />)}
								<Grid item md={12} order={{ xs: 4 }} sx={{ zIndex: '10' }}>
									<div className='fondo-mobile-blanco'></div>
									<PaginationIndex
										data-apartado={`${tof} ${tpf}`}
										totalPropiedades={propertiesCountState}
									/>
								</Grid>
							</Grid>
							<Grid item xs={12} display={{ xs: 'block', md: 'none' }} order={{ xs: 5 }} sx={{ zIndex: '10' }}>
								{mobileDevice() && <TextoSeo seoText={seo.seoText} />}
								<BannerSimple lateral={currentBanners.lateral} />
							</Grid>
						</Grid>
					</Grid>
				</main>
			</ContainerStyled>

			{showGuestSignUpModal && <GuestSignUpModal isMobile={mobileDevice()} leadStatus={leadStatus} showModal={showGuestSignUpModal} guestSignUpModalHandler={guestSignUpModalHandler} selectedPropertyData={selectedPropertyData} generateLead={generateLead} saveTempLogInData={saveTempLogInData} listaSeoUrl={pathCanonical.join('/')} />}
			{showQuoteStatusModal && <QuoteStatusModal isMobile={mobileDevice()} showModal={showQuoteStatusModal} quoteStatusModalHandler={quoteStatusModalHandler} selectedPropertyData={selectedPropertyData} leadStatusInfo={leadStatusInfo} />}
			{showContactStatusModal && <ContactStatusModal isMobile={mobileDevice()} showModal={showContactStatusModal} contactStatusModalHandler={contactStatusModalHandler} leadStatusInfo={leadStatusInfo} userData={userData} />}

			{computedRegionSlug && (
				<Prefooter regionContent={computedRegionSlug} />
			)}

			<FooterIndex footerData={footerData} />
		</>
	)
}

ListaSEO.getLayout = getPersonasLayout;
export default ListaSEO
